/**
 * Controls enforcement of widget boundaries to ensure
 * widgets stay within the viewport
 */
export class WidgetBoundaryController {
    constructor(settings = {}) {
        // Client dimensions
        Object.defineProperty(this, "viewportWidth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "viewportHeight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // Boundary settings
        Object.defineProperty(this, "settings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Apply default settings
        this.settings = {
            minWidth: settings.minWidth ?? WidgetBoundaryController.DEFAULT_MIN_WIDTH,
            minHeight: settings.minHeight ?? WidgetBoundaryController.DEFAULT_MIN_HEIGHT,
            maxWidth: settings.maxWidth,
            maxHeight: settings.maxHeight,
            allowOverflow: settings.allowOverflow ?? false,
        };
        this.updateViewportDimensions();
    }
    /**
     * Updates the internal viewport dimensions
     */
    updateViewportDimensions() {
        this.viewportWidth = document.documentElement.clientWidth;
        this.viewportHeight = document.documentElement.clientHeight;
    }
    /**
     * Gets the current viewport size
     */
    getViewportSize() {
        return {
            width: this.viewportWidth,
            height: this.viewportHeight,
        };
    }
    /**
     * Enforces minimum and maximum dimensions on a size
     */
    enforceMinimumSize(size) {
        const constrainedSize = {
            width: Math.max(size.width, this.settings.minWidth),
            height: Math.max(size.height, this.settings.minHeight),
        };
        // Apply maximum constraints if they exist
        if (this.settings.maxWidth !== undefined) {
            constrainedSize.width = Math.min(constrainedSize.width, this.settings.maxWidth);
        }
        if (this.settings.maxHeight !== undefined) {
            constrainedSize.height = Math.min(constrainedSize.height, this.settings.maxHeight);
        }
        return constrainedSize;
    }
    /**
     * Enforces size constraints including both min and max dimensions
     */
    enforceSizeConstraints(size) {
        return this.enforceMinimumSize(size);
    }
    /**
     * Constrains a widget position to keep it within viewport boundaries
     */
    constrainPosition(position, size) {
        if (this.settings.allowOverflow) {
            return { ...position };
        }
        const maxX = this.viewportWidth - size.width;
        const maxY = this.viewportHeight - size.height;
        return {
            x: Math.max(0, Math.min(position.x, maxX)),
            y: Math.max(0, Math.min(position.y, maxY)),
        };
    }
    /**
     * Enforces boundaries for drag operations
     */
    enforceDragBoundaries(position, size) {
        return this.constrainPosition(position, size);
    }
    /**
     * Enforces all boundaries (position and size) for resize operations
     */
    enforceResizeBoundaries(position, size) {
        // First enforce size constraints (min and max)
        const newSize = this.enforceSizeConstraints(size);
        const newPosition = { ...position };
        if (this.settings.allowOverflow) {
            return { position: newPosition, size: newSize };
        }
        // Handle left edge overflow
        if (newPosition.x < 0) {
            // Adjust width and set x to 0
            newSize.width += newPosition.x; // This reduces width by the negative amount
            newPosition.x = 0;
            // Re-enforce size constraints
            const constrainedSize = this.enforceSizeConstraints({
                width: newSize.width,
                height: newSize.height,
            });
            newSize.width = constrainedSize.width;
        }
        // Handle top edge overflow
        if (newPosition.y < 0) {
            // Adjust height and set y to 0
            newSize.height += newPosition.y; // This reduces height by the negative amount
            newPosition.y = 0;
            // Re-enforce size constraints
            const constrainedSize = this.enforceSizeConstraints({
                width: newSize.width,
                height: newSize.height,
            });
            newSize.height = constrainedSize.height;
        }
        // Handle right edge overflow
        if (newPosition.x + newSize.width > this.viewportWidth) {
            newSize.width = this.viewportWidth - newPosition.x;
            // Re-enforce size constraints - might require adjusting x
            const constrainedSize = this.enforceSizeConstraints({
                width: newSize.width,
                height: newSize.height,
            });
            if (constrainedSize.width > newSize.width) {
                newPosition.x = Math.max(0, this.viewportWidth - constrainedSize.width);
                newSize.width = constrainedSize.width;
            }
        }
        // Handle bottom edge overflow
        if (newPosition.y + newSize.height > this.viewportHeight) {
            newSize.height = this.viewportHeight - newPosition.y;
            // Re-enforce size constraints - might require adjusting y
            const constrainedSize = this.enforceSizeConstraints({
                width: newSize.width,
                height: newSize.height,
            });
            if (constrainedSize.height > newSize.height) {
                newPosition.y = Math.max(0, this.viewportHeight - constrainedSize.height);
                newSize.height = constrainedSize.height;
            }
        }
        return { position: newPosition, size: newSize };
    }
    /**
     * Enforces all boundaries for a widget
     * Used when window resizes to ensure widget stays within viewport
     */
    enforceAllBoundaries(position, size) {
        return this.enforceResizeBoundaries(position, size);
    }
}
// Default minimum dimensions
Object.defineProperty(WidgetBoundaryController, "DEFAULT_MIN_WIDTH", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 200
});
Object.defineProperty(WidgetBoundaryController, "DEFAULT_MIN_HEIGHT", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 150
});
//# sourceMappingURL=WidgetBoundaryController.js.map